/**
 * The spotlight: a scrim over the page with a hole cut around one element.
 *
 * THE ONLY FILE THAT IMPORTS driver.js (spec 25 section 3.1). The engine, the
 * definitions, the host and the card never see the library or its option
 * names, so if it is abandoned or starts fighting the design system, this file
 * is rewritten and nothing else is.
 *
 * driver.js is used for the geometry only: the scrim, the cut-out, keeping
 * both right while the page scrolls and resizes, and scrolling the target into
 * view. Its own popover is never shown. The card is ours (LwTourCard.vue,
 * placed by TourHost), because the library's popover carries its own colours
 * and this codebase has a token-purity gate for a reason.
 *
 * The target stays CLICKABLE (`disableActiveInteraction: false`) and the scrim
 * never closes anything when clicked (`allowClose: false`): the page is never
 * made inert, and leaving the tour is a decision the card offers, not an
 * accident.
 */
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { renderer } from '../training/render';

let instance = null;

function reducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function ensure() {
  if (!instance) {
    instance = driver({
      animate: !reducedMotion(),
      // We scroll (show(), below). The library centres its target on BOTH axes,
      // and this app's document is a few pixels wider than the viewport, so its
      // scroll dragged the whole shell sideways (seen on the live demo).
      smoothScroll: false,
      allowClose: false,
      allowKeyboardControl: false, // the host owns the keyboard
      disableActiveInteraction: false,
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 6,
    });
  }
  return instance;
}

/** Cut the hole around `element`. */
export function show(element, { scroll = true } = {}) {
  if (!element) return hide();
  // Vertically centred, horizontally left alone, and instant: the cut-out is
  // measured right after, and a smooth scroll would still be moving. The host
  // passes `scroll: false` when it has already put the element where it wants
  // it (a phone, where the docked card takes the bottom of the screen).
  if (scroll && typeof element.scrollIntoView === 'function') {
    element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
  }
  // Filming a training video: the bare app, no scrim (Mark, 2026-10-27: Relay
  // shows what matters; training/render.js).
  if (renderer()) return;
  ensure().highlight({ element });
}

/** Re-measure the hole: the page under it moved (a late scroll, late content). */
export function refresh() {
  if (instance && instance.isActive()) instance.refresh();
}

/** Take the scrim away, keep the instance (the next step will want it). */
export function hide() {
  if (instance && instance.isActive()) instance.destroy();
  instance = null;
}

export function destroy() {
  hide();
}
